import { getTenantConfig } from '../../config/tenantConfig';
import httpClient from './httpClient';

/**
 * Person Management API Service
 * Handles CRUD operations for Person and AssociatedPerson tables
 * Uses httpClient for automatic 401 handling and token refresh
 */

// ============================================================================
// PERSON OPERATIONS
// ============================================================================

/**
 * Get all persons for current tenant
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of results
 * @param {string} options.dataset - Filter by dataset ID
 * @returns {Promise<Array>} Array of person objects
 */
export async function listPersons(options = {}) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      console.error('Person Management API endpoint not configured');
      throw new Error('Person Management API not configured. Run bin/frontend/configure-api-endpoints.sh');
    }

    const params = {};
    if (options.limit) params.limit = options.limit;
    if (options.dataset) params.dataset = options.dataset;

    const result = await httpClient.get(`${personApi}/persons`, { params });
    return result.persons || [];
  } catch (error) {
    console.error('Error fetching persons:', error);
    throw error;
  }
}

/**
 * Search persons with filters
 * @param {Object} filters - Search filters
 * @param {string} filters.search - Text search (name, constituency)
 * @param {string} filters.gender - Gender filter
 * @param {string} filters.party - Political party
 * @param {string} filters.dataset - Dataset ID
 * @param {string} filters.persontype - Person type (MP, LORD, etc.)
 * @param {string} filters.agegroup - Age group
 * @param {string} filters.interest_level - Interest level
 * @param {number} filters.limit - Maximum results
 * @returns {Promise<Array>} Array of matching persons
 */
export async function searchPersons(filters = {}) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.gender) params.gender = filters.gender;
    if (filters.party) params.party = filters.party;
    if (filters.dataset) params.dataset = filters.dataset;
    if (filters.persontype) params.persontype = filters.persontype;
    if (filters.agegroup) params.agegroup = filters.agegroup;
    if (filters.interest_level) params.interest_level = filters.interest_level;
    if (filters.limit) params.limit = filters.limit;

    const result = await httpClient.get(`${personApi}/persons/search`, { params });
    return result.persons || [];
  } catch (error) {
    console.error('Error searching persons:', error);
    throw error;
  }
}

/**
 * Get a specific person by ID
 * @param {string} personId - Person ID
 * @returns {Promise<Object>} Person object
 */
export async function getPerson(personId) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.get(`${personApi}/persons/${personId}`);
  } catch (error) {
    console.error(`Error fetching person ${personId}:`, error);
    throw error;
  }
}

/**
 * Create a new person
 * @param {Object} personData - Person creation data
 * @param {string} personData.person_id - Unique person ID
 * @param {string} personData.first_name - First name
 * @param {string} personData.last_name - Last name
 * @param {string} personData.dataset - Dataset ID
 * @param {File} personData.image - Face image file
 * @returns {Promise<Object>} Created person object with Rekognition face_id
 */
export async function createPerson(personData) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.post(`${personApi}/persons`, personData);
  } catch (error) {
    console.error('Error creating person:', error);
    throw error;
  }
}

/**
 * Update person metadata
 * @param {string} personId - Person ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated person object
 */
export async function updatePerson(personId, updates) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.put(`${personApi}/persons/${personId}`, updates);
  } catch (error) {
    console.error(`Error updating person ${personId}:`, error);
    throw error;
  }
}

/**
 * Delete a person and their Rekognition face
 * @param {string} personId - Person ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export async function deletePerson(personId) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.delete(`${personApi}/persons/${personId}`);
  } catch (error) {
    console.error(`Error deleting person ${personId}:`, error);
    throw error;
  }
}

// ============================================================================
// ASSOCIATED PERSON OPERATIONS
// ============================================================================

/**
 * Get all associated persons for current tenant
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of results
 * @param {number} options.min_sightings - Minimum sighting count
 * @returns {Promise<Array>} Array of associated person objects
 */
export async function listAssociatedPersons(options = {}) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      console.error('Person Management API endpoint not configured');
      throw new Error('Person Management API not configured. Run bin/frontend/configure-api-endpoints.sh');
    }

    const params = {};
    if (options.limit) params.limit = options.limit;
    if (options.min_sightings) params.min_sightings = options.min_sightings;

    const result = await httpClient.get(`${personApi}/associated-persons`, { params });
    return result.associated_persons || [];
  } catch (error) {
    console.error('Error fetching associated persons:', error);
    throw error;
  }
}

/**
 * Search associated persons with filters
 * @param {Object} filters - Search filters
 * @param {number} filters.min_sightings - Minimum sighting count
 * @param {number} filters.max_sightings - Maximum sighting count
 * @param {string} filters.first_seen_after - ISO date (YYYY-MM-DD)
 * @param {string} filters.last_seen_after - ISO date
 * @param {string} filters.last_seen_before - ISO date
 * @param {string} filters.gender - Gender
 * @param {string} filters.agegroup - Age group
 * @param {string} filters.height - Height
 * @param {string} filters.build - Build
 * @param {string} filters.skintone - Skin tone
 * @param {string} filters.eyecolor - Eye color
 * @param {string} filters.haircolor - Hair color
 * @param {string} filters.hairstyle - Hair style
 * @param {string} filters.facialhair - Facial hair
 * @param {string} filters.glasses - Glasses
 * @param {string} filters.dress - Dress/clothing
 * @param {number} filters.limit - Maximum results
 * @returns {Promise<Array>} Array of matching associated persons
 */
export async function searchAssociatedPersons(filters = {}) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    const params = {};
    if (filters.min_sightings) params.min_sightings = filters.min_sightings;
    if (filters.max_sightings) params.max_sightings = filters.max_sightings;
    if (filters.first_seen_after) params.first_seen_after = filters.first_seen_after;
    if (filters.last_seen_after) params.last_seen_after = filters.last_seen_after;
    if (filters.last_seen_before) params.last_seen_before = filters.last_seen_before;
    if (filters.gender) params.gender = filters.gender;
    if (filters.agegroup) params.agegroup = filters.agegroup;
    if (filters.height) params.height = filters.height;
    if (filters.build) params.build = filters.build;
    if (filters.skintone) params.skintone = filters.skintone;
    if (filters.eyecolor) params.eyecolor = filters.eyecolor;
    if (filters.haircolor) params.haircolor = filters.haircolor;
    if (filters.hairstyle) params.hairstyle = filters.hairstyle;
    if (filters.facialhair) params.facialhair = filters.facialhair;
    if (filters.glasses) params.glasses = filters.glasses;
    if (filters.dress) params.dress = filters.dress;
    if (filters.limit) params.limit = filters.limit;

    const result = await httpClient.get(`${personApi}/associated-persons/search`, { params });
    return result.associated_persons || [];
  } catch (error) {
    console.error('Error searching associated persons:', error);
    throw error;
  }
}

/**
 * Get a specific associated person by ID
 * @param {string} personId - Associated person ID
 * @returns {Promise<Object>} Associated person object with sightings
 */
export async function getAssociatedPerson(personId) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.get(`${personApi}/associated-persons/${personId}`);
  } catch (error) {
    console.error(`Error fetching associated person ${personId}:`, error);
    throw error;
  }
}

/**
 * Update associated person metadata or characteristics
 * @param {string} personId - Associated person ID
 * @param {Object} updates - Fields to update (notes, characteristics, etc.)
 * @returns {Promise<Object>} Updated associated person object
 */
export async function updateAssociatedPerson(personId, updates) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.put(`${personApi}/associated-persons/${personId}`, updates);
  } catch (error) {
    console.error(`Error updating associated person ${personId}:`, error);
    throw error;
  }
}

/**
 * Delete an associated person and their Rekognition face
 * @param {string} personId - Associated person ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export async function deleteAssociatedPerson(personId) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.delete(`${personApi}/associated-persons/${personId}`);
  } catch (error) {
    console.error(`Error deleting associated person ${personId}:`, error);
    throw error;
  }
}

/**
 * Promote associated person to known person
 * Moves face from associated collection to known collection
 * @param {string} personId - Associated person ID
 * @param {Object} personData - New person data
 * @param {string} personData.person_id - New person ID for promoted person
 * @param {string} personData.first_name - First name
 * @param {string} personData.last_name - Last name
 * @param {string} personData.dataset - Dataset ID
 * @returns {Promise<Object>} Object with newPerson and deletedAssociatedPerson
 */
export async function promoteAssociatedPerson(personId, personData) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.post(`${personApi}/associated-persons/${personId}/promote`, personData);
  } catch (error) {
    console.error(`Error promoting associated person ${personId}:`, error);
    throw error;
  }
}

/**
 * Detect face attributes for an associated person
 * Used before promotion to pre-fill demographics
 * @param {string} personId - Associated person ID
 * @returns {Promise<Object>} Detected attributes (gender, agegroup, glasses, facialhair)
 */
export async function detectAssociatedPersonAttributes(personId) {
  try {
    const config = getTenantConfig();
    const personApi = config.api.personManagement;

    if (!personApi) {
      throw new Error('Person Management API not configured');
    }

    return await httpClient.get(`${personApi}/associated-persons/${personId}/attributes`);
  } catch (error) {
    console.error(`Error detecting attributes for associated person ${personId}:`, error);
    throw error;
  }
}
